import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../DataGridFlexSales';

import {
  reportCancellationsFilterGroupBySelector,
  reportCancellationsFilterSelector,
  reportCancellationsSelector,
  reportFilterCountryCodeSelector,
  downloadReportPdf,
} from '../../state';
import { Cancellation } from '../../types';
import handlerFactory, { CancellationDataGridContext } from './handlerFactory';

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<Cancellation, CancellationDataGridContext>();

  const { t } = useTranslation();
  const title = t('cancellations.title');
  const country = useSelector(reportFilterCountryCodeSelector);
  const cancellationsResource = useSelector(reportCancellationsSelector);
  const groupBy = useSelector(reportCancellationsFilterGroupBySelector);
  const reportCancellationsFilters = useSelector(reportCancellationsFilterSelector);

  const { data, loading } = cancellationsResource;

  const gridContext = useMemo(
    () => ({
      t,
      country,
      groupBy,
    }),
    [t, country, groupBy]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title,
    loading,
    filters: reportCancellationsFilters,
  });

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<Cancellation, CancellationDataGridContext>();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    handler.setExportConfig({
      PDF: {
        generator: (table, title) => {
          dispatch(
            downloadReportPdf({
              locale: handler.locale,
              t,
              table,
              title,
              includeMissingClosingTable: false,
              parameters: [
                'countryCode',
                'stores',
                'pos',
                'salesTypes',
                'segments',
                'timeBand',
                'hourRange',
                'startAccountingDate',
                'endAccountingDate',
                'startOperationDate',
                'endOperationDate',
                'startBusinessDate',
                'endBusinessDate',
                'groupBy',
              ],
              opStructures: [
                'regionalMgmt',
                'region',
                'management',
                'supervision',
                'agreement',
                'company',
              ],
            })
          );
        },
        isDispatchAction: true,
      },
    });
  }, [handler, dispatch, t]);

  return null;
};

const CancellationsDataGrid = () => (
  <DataGridFlexSales handlerFactory={handlerFactory}>
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default CancellationsDataGrid;
